import { useEffect, useMemo, useRef, type RefObject } from 'react';
import useClickOutside from 'lib/hooks/useClickOutside';
import type { SectionMode } from './types';
import styles from './section-mode-menu.module.scss';

/** The two construction methods, with the copy the mock uses for each. */
const SECTION_MODES: Array<{
  id: SectionMode;
  label: string;
  prompt: string;
}> = [
  {
    id: 'arbitrary_line',
    label: 'Arbitrary Line',
    prompt:
      'Click the map to draw the section, point by point. Vertices can be dragged afterwards.',
  },
  {
    id: 'well_to_well',
    label: 'Well to Well',
    prompt:
      'Click vertical wells to build the section. It runs between them in the order picked.',
  },
];

export interface SectionModeMenuProps {
  onPick: (mode: SectionMode) => void;
  onCancel: () => void;
  /**
   * The control that opened the menu. Counted as inside, so pressing it closes the
   * menu through its own toggle rather than being dismissed here and immediately
   * reopened by the click that follows.
   */
  anchorRef?: RefObject<Node>;
}

/**
 * Asks which method should build the plane, before the map goes live.
 *
 * The choice has to come first because a click means different things in the two
 * modes — a vertex in one, a well in the other — so there is no safe default.
 *
 * Deliberately not modal: `aria-modal="false"` and no focus trap, because the map
 * stays visible and live behind the menu. Focus moves in so the keyboard can pick,
 * but taking it hostage would claim the rest of the page is inert when it is not.
 *
 * @param {SectionModeMenuProps} props - Component props.
 * @return {JSX.Element} The mode menu.
 */
export const SectionModeMenu = ({
  onPick,
  onCancel,
  anchorRef,
}: SectionModeMenuProps): JSX.Element => {
  const menuRef = useRef<HTMLDivElement>(null);
  const dismissRefs = useMemo(
    () => (anchorRef ? [menuRef, anchorRef] : [menuRef]),
    [anchorRef]
  );

  // Dismissal runs on mousedown, not click: the map places a vertex on its own
  // mousedown, so waiting for a click would let the press through first.
  useClickOutside({ refs: dismissRefs, onClickOutside: onCancel });

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCancel();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onCancel]);

  // Focus moves in without trapping, so the first option is one Tab away rather
  // than requiring a pointer.
  useEffect(() => {
    menuRef.current?.querySelector('button')?.focus();
  }, []);

  return (
    <div
      ref={menuRef}
      className={styles.menu}
      role="dialog"
      aria-modal="false"
      aria-label="New cross section"
    >
      <p className={styles.title}>Generate a cross section by</p>
      {SECTION_MODES.map((mode) => (
        <button
          key={mode.id}
          type="button"
          className={styles.option}
          onClick={() => onPick(mode.id)}
        >
          <span className={styles.optionLabel}>{mode.label}</span>
          <span className={styles.optionHint}>{mode.prompt}</span>
        </button>
      ))}
    </div>
  );
};
